import { Component } from 'react';

import observer from 'saddlebag-observer';

import Navigation from './Navigation';
import NavigationState from './navigation-state';

import type {
  NavElements,
  ContainerOffset,
  NavigationStateType,
} from './navigation-state';
import type { Configs } from '@shared/types/configs';
import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';
import type { Nullable, Stay } from '@shared/types/utils';
import type { Destination } from '@shared/ui/types';
import type { NavigationItems } from '@shared/utils-frontend/navigation-service';

import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';
import {
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoViewButtonClicked } from '@shared/utils-frontend/mini-events/hotels-action';
import {
  REVIEWS,
  NAVIGATION_ITEMS,
  scrollToElement,
  scrollToRoomGroupingResult,
  AMENITIES,
} from '@shared/utils-frontend/navigation-service';
import { getScrollY } from '@shared/utils-frontend/window';

const NAVIGATION_CONTAINER_HEIGHT = 80;
const STICKY_SEARCH_HEIGHT = 104;
const CLICK_AREA_MAPPING = {
  [REVIEWS]: CLICK_AREA.REVIEWS_TAB,
  [AMENITIES]: CLICK_AREA.AMENITIES_TAB,
};
type Props = {
  i18n: I18nService;
  items: NavigationItems;
  miniEventTracker: MiniEventTracker;
  destination: Destination;
  userContext: UserContext;
  configs: Configs;
  stay: Stay;
};

type State = {
  selected: Nullable<string>;
  needFixed: boolean;
  showFilters: boolean;
  isReviewsEmpty: boolean;
  priceIndex: number;
};

class NavigationContainer extends Component<Props, State> {
  navElements: Nullable<NavElements>;

  containerOffset: ContainerOffset;

  navigationState: Nullable<NavigationStateType>;

  setReviewsResultHandler: Nullable<Function>;

  seoVariantConfig: boolean;

  constructor(props: Props) {
    super(props);

    this.state = {
      selected: NAVIGATION_ITEMS[0].id,
      needFixed: false,
      showFilters: false,
      isReviewsEmpty: false,
      priceIndex: 0,
    };
    this.navElements = null;
    this.containerOffset = {
      offsetHeight: 0,
      offsetTop: 0,
    };
    this.seoVariantConfig =
      props.userContext.enableSEOVariantHotelsDetailsPage &&
      props.configs.showStickySearchControlsOnHotelDetailsSEO;

    this.navigationState = null;

    this.setReviewsResultHandler = null;
  }

  componentDidMount() {
    const { items } = this.props;
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.navElements = items.map(({ anchorName, id }) => ({
      id,
      element: document.getElementById(anchorName),
    }));
    this.navigationState = NavigationState({
      navElements: this.navElements,
      containerOffset: this.containerOffset,
    });

    this.setReviewsResultHandler = observer.subscribe(
      'hotel-reviews-result-is-empty',
      ({ isReviewsEmpty }: { isReviewsEmpty: boolean }) => {
        this.setState({ isReviewsEmpty });
      },
    );
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll);
    observer.unsubscribe(this.setReviewsResultHandler);
  }

  onScroll = () => {
    window.requestAnimationFrame(this.onRequestAnimationFrame);
  };

  onRequestAnimationFrame = () => {
    if (!this.containerOffset || !this.navElements || !this.navigationState) {
      return;
    }

    const scrollY = getScrollY();
    const state = {
      needFixed: this.navigationState.getNeedFixed(scrollY),
      selected: this.navigationState.getSelected(scrollY + this.getOffset()),
      showFilters: this.navigationState.getShowFilters(scrollY, 200),
    };
    const needToUpdateStates = Object.keys(state)
      // @ts-expect-error missing properties temporarily
      .filter((key) => state[key] !== this.state[key])
      // @ts-expect-error missing properties temporarily
      .reduce((acc, key) => ({ ...acc, [key]: state[key] }), {});

    if (Object.keys(needToUpdateStates).length) {
      this.setState(state);
      if (state.selected === REVIEWS) {
        this.props.miniEventTracker.trackHotelsAction(
          ACTION_TYPE.DETAILS_REVIEW_SECTION_SEEN,
        );
      }
    }
  };

  onClick = (anchorName: string, id: string) => {
    if (id === REVIEWS || id === AMENITIES) {
      this.props.miniEventTracker.trackHotelsAction(
        ACTION_TYPE.VIEW_BUTTON_CLICKED,
        buildAdditionalInfoViewButtonClicked(CLICK_AREA_MAPPING[id]),
      );
    }

    scrollToElement(
      anchorName,
      this.containerOffset.offsetHeight + this.getOffset(),
    );
  };

  refCallback = (element: HTMLElement) => {
    if (element && element.offsetTop) {
      const { offsetHeight, offsetTop } = element;
      this.containerOffset = { offsetTop, offsetHeight };
    }
  };

  getNavigationItems = () => {
    const { items } = this.props;
    const { isReviewsEmpty } = this.state;
    if (isReviewsEmpty) {
      return items.map((item) =>
        item.id === REVIEWS ? { ...item, isShow: false } : item,
      );
    }
    return items;
  };

  getOffset = (): number => {
    const { needFixed } = this.state;

    if (this.seoVariantConfig) {
      return needFixed
        ? STICKY_SEARCH_HEIGHT
        : NAVIGATION_CONTAINER_HEIGHT + STICKY_SEARCH_HEIGHT;
    }

    if (!needFixed) {
      return NAVIGATION_CONTAINER_HEIGHT;
    }

    return 0;
  };

  render() {
    const { needFixed, priceIndex, selected, showFilters } = this.state;
    const { destination, i18n, stay, ...rest } = this.props;
    const navigationItems = this.getNavigationItems();

    return (
      <Navigation
        needFixed={needFixed}
        showFilters={showFilters}
        selected={selected}
        onClick={this.onClick}
        refCallback={this.refCallback}
        applyFilters={scrollToRoomGroupingResult}
        height={this.containerOffset && this.containerOffset.offsetHeight}
        {...rest}
        items={navigationItems}
        stay={stay}
        priceIndex={priceIndex}
        destination={destination}
      />
    );
  }
}

export default withMiniEventTracker(
  withUserContext(withConfiguration(withI18n(NavigationContainer))),
);
